import type { KeywordRecommendationAction } from '@/types/keywordRecommendations';
import type { RecommendationSortField, RecommendationSortOrder } from '@/api/recommendationUtils';

type RecommendationTableToolbarProps = {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  actionFilter: KeywordRecommendationAction | 'ALL';
  onActionFilterChange: (value: KeywordRecommendationAction | 'ALL') => void;
  sortBy: RecommendationSortField;
  sortOrder: RecommendationSortOrder;
  onSortByChange: (value: RecommendationSortField) => void;
  onSortOrderChange: (value: RecommendationSortOrder) => void;
  showingCount: number;
  totalCount: number;
  searchPlaceholder?: string;
  showActionFilter?: boolean;
};

const SORT_OPTIONS: { value: RecommendationSortField; label: string }[] = [
  { value: 'cost', label: 'Spend' },
  { value: 'sales30d', label: 'Sales' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'roas', label: 'ROAS' },
];

export function RecommendationTableToolbar({
  searchQuery,
  onSearchChange,
  actionFilter,
  onActionFilterChange,
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderChange,
  showingCount,
  totalCount,
  searchPlaceholder = 'Search…',
  showActionFilter = false,
}: RecommendationTableToolbarProps) {
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-border bg-page/80 px-3 py-2">
      <div className="min-w-[12rem] flex-1">
        <label className="mb-1 block text-xs font-medium text-foreground-muted">Search</label>
        <input
          type="search"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
        />
      </div>

      {showActionFilter ? (
        <div>
          <label className="mb-1 block text-xs font-medium text-foreground-muted">Action</label>
          <select
            value={actionFilter}
            onChange={(event) =>
              onActionFilterChange(event.target.value as KeywordRecommendationAction | 'ALL')
            }
            className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
          >
            <option value="ALL">All actions</option>
            <option value="ADD_KEYWORD">Add keyword</option>
            <option value="ADD_NEGATIVE_KEYWORD">Add negative</option>
          </select>
        </div>
      ) : null}

      <div>
        <label className="mb-1 block text-xs font-medium text-foreground-muted">Sort by</label>
        <div className="flex gap-1">
          <select
            value={sortBy}
            onChange={(event) => onSortByChange(event.target.value as RecommendationSortField)}
            className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={sortOrder}
            onChange={(event) => onSortOrderChange(event.target.value as RecommendationSortOrder)}
            className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
          >
            <option value="desc">High → low</option>
            <option value="asc">Low → high</option>
          </select>
        </div>
      </div>

      <p className="pb-1.5 text-sm text-foreground-muted">
        Showing {showingCount} of {totalCount}
        {totalCount >= 50 ? ' (top 50 by spend from API)' : ''}
      </p>
    </div>
  );
}
